'use client';

import { DownloadRecordType } from '@velocity-exchange/common';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import Button from 'src/components/primitives/Button/Button';
import Select from 'src/components/primitives/Select/Select';
import { cj } from 'src/utils/classNames';
import { AssetFilterWithSearch } from 'src/components/Inputs/AssetFilterWithSearch';
import MultiSelect from 'src/components/primitives/MultiSelect/MultiSelect';
import useExportHistoryModal from 'src/components/Modals/ExportHistoryModal/hooks/useExportHistoryModal';
import { getIsExportHistoryUserType } from 'src/components/Modals/ExportHistoryModal/utils';

export interface ExportHistoryModalProps {
	type?: DownloadRecordType;
}

export default function ExportHistoryModal({
	type = 'trades',
}: ExportHistoryModalProps) {
	const {
		exportType,
		selectedMarket,
		setSelectedMarket,
		selectedPeriod,
		selectedMonth,
		accountOptions,
		accountSelectDisplayValue,
		selectedAccounts,
		setSelectedAccounts,
		allAccountsSelected,
		handleAllAccountsSelectedChange,
		exportTypeSelectUtils,
		exportPeriodSelectUtils,
		exportMonthSelectUtils,
		exportDaySelectUtils,
		assetFilterOptions,
		isSubmitDisabled,
		isMarketTradesExceedsLimit,
		isSubmitting,
		handleSubmit,
		handleClose,
	} = useExportHistoryModal({ type });

	return (
		<BaseModal.Container
			onClose={handleClose}
			dataTestId="export-history-modal"
		>
			<BaseModal.Title
				onClose={handleClose}
				closeButtonTestId="export-history-modal-close"
			>
				Export Statement
			</BaseModal.Title>

			<BaseModal.Body>
				<Select.Root
					open={exportTypeSelectUtils.open}
					onOpenChange={exportTypeSelectUtils.onOpenChange}
					value={exportTypeSelectUtils.value}
					onValueChange={exportTypeSelectUtils.onValueChange}
				>
					<Select.Trigger>
						{exportTypeSelectUtils.selectedOption?.label}
					</Select.Trigger>
					<Select.Options scrollContainerClassName="max-h-50">
						{exportTypeSelectUtils.selectOptions.map((option) => (
							<Select.Option key={option.value} value={option.value}>
								{option.label}
							</Select.Option>
						))}
					</Select.Options>
				</Select.Root>

				{getIsExportHistoryUserType(exportType) && (
					<MultiSelect.Root
						value={selectedAccounts}
						setValue={setSelectedAccounts}
					>
						<MultiSelect.Trigger label="Account">
							{accountSelectDisplayValue}
						</MultiSelect.Trigger>
						<MultiSelect.Options
							scrollContainerClassName="max-h-50"
							allSelected={allAccountsSelected}
							onAllSelectedChange={handleAllAccountsSelectedChange}
						>
							{accountOptions.map((option) => (
								<MultiSelect.Option
									key={option.value}
									value={option.value}
									isSelected={option.isSelected}
								>
									{option.label}
								</MultiSelect.Option>
							))}
						</MultiSelect.Options>
					</MultiSelect.Root>
				)}

				<Select.Root
					open={exportPeriodSelectUtils.open}
					onOpenChange={exportPeriodSelectUtils.onOpenChange}
					value={exportPeriodSelectUtils.value}
					onValueChange={exportPeriodSelectUtils.onValueChange}
				>
					<Select.Trigger label="Date">
						{exportPeriodSelectUtils.selectedOption?.label}
					</Select.Trigger>
					<Select.Options scrollContainerClassName="max-h-50">
						{exportPeriodSelectUtils.selectOptions.map((option) => (
							<Select.Option key={option.value} value={option.value}>
								{option.label}
							</Select.Option>
						))}
					</Select.Options>
				</Select.Root>

				{selectedPeriod.includes('custom') && (
					<div
						className={cj(
							'grid w-full gap-x-lg',
							selectedMonth === 0 ? 'grid-cols-1' : 'grid-cols-2'
						)}
					>
						<Select.Root
							open={exportMonthSelectUtils.open}
							onOpenChange={exportMonthSelectUtils.onOpenChange}
							value={exportMonthSelectUtils.value}
							onValueChange={exportMonthSelectUtils.onValueChange}
						>
							<Select.Trigger label="Month">
								{exportMonthSelectUtils.selectedOption?.label}
							</Select.Trigger>
							<Select.Options scrollContainerClassName="max-h-40">
								{exportMonthSelectUtils.selectOptions.map((option) => (
									<Select.Option key={option.value} value={option.value}>
										{option.label}
									</Select.Option>
								))}
							</Select.Options>
						</Select.Root>

						{selectedMonth !== 0 && (
							<Select.Root
								open={exportDaySelectUtils.open}
								onOpenChange={exportDaySelectUtils.onOpenChange}
								value={exportDaySelectUtils.value}
								onValueChange={exportDaySelectUtils.onValueChange}
							>
								<Select.Trigger label="Day">
									{exportDaySelectUtils.selectedOption?.label}
								</Select.Trigger>
								<Select.Options scrollContainerClassName="max-h-40">
									{exportDaySelectUtils.selectOptions.map((option) => (
										<Select.Option key={option.value} value={option.value}>
											{option.label}
										</Select.Option>
									))}
								</Select.Options>
							</Select.Root>
						)}
					</div>
				)}

				{(exportType == 'market-trades' || exportType == 'funding-rates') && (
					<AssetFilterWithSearch
						label="Market"
						scrollContainerClassName="max-h-50"
						currentSelection={selectedMarket}
						options={assetFilterOptions}
						onChange={setSelectedMarket}
						id="marketDownloadSelector"
					/>
				)}

				{isMarketTradesExceedsLimit && (
					<InlineAlert variant="error" title="Time period exceeds limit">
						Market trades exports are limited to 31 days. Please select a
						specific month.
					</InlineAlert>
				)}

				<InlineAlert variant="info" title="Generating statement may take time">
					You can still navigate away and close the tab. When your statement is
					ready, you can download it from the Export page.
				</InlineAlert>
			</BaseModal.Body>

			<BaseModal.Footer>
				<Button
					size="lg"
					variant="primary"
					className="w-full"
					onClick={handleSubmit}
					disabled={isSubmitDisabled}
					isLoading={isSubmitting}
					dataTestId="export-history-modal-submit"
				>
					Export Statement
				</Button>
			</BaseModal.Footer>
		</BaseModal.Container>
	);
}
